import React, { useEffect } from "react";
import DatePicker from "react-datepicker";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { formatISODate } from "../../services/receipt.service";
import "../receipts/receiptModule.css";

/* ------------------------------------------------------------------ */
/* Validation rules                                                    */
/* ------------------------------------------------------------------ */

const digitsOnly = /^[0-9]+$/;
// letters, spaces and the punctuation real bank names use: "St. Bank of India", "J&K Bank", "HDFC Bank (Ltd)"
const bankNameRegex = /^[A-Za-z][A-Za-z\s.&()'-]*$/;
const upiOrVpaRegex = /^(?:\d{10}|[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64})$/;

const bankNameRule = Yup.string()
    .trim()
    .matches(bankNameRegex, "Bank Name must contain only letters")
    .max(100, "Must be 100 characters or less")
    .required("Bank Name is required");

// cq_dc_utr_no is a Long in the database, so numbers are limited to 18 digits.
const numberRule = (label) =>
    Yup.string()
        .trim()
        .matches(digitsOnly, `${label} must be numeric without special characters`)
        .max(18, `${label} must be 18 digits or less`)
        .required(`${label} is required`);

/* ------------------------------------------------------------------ */
/* One config per payment type: title, initial values, schema, fields  */
/* The saved keys (chequeNo, chequeDate, bankName, rtgsNo ...) are the */
/* same as before, so the backend and the preview are unaffected.      */
/* ------------------------------------------------------------------ */

const PAYMENT_FORMS = {
    CQ: {
        title: "Enter Cheque Details",
        initial: { chequeNo: "", chequeDate: null, bankName: "" },
        schema: Yup.object({
            chequeNo: numberRule("Cheque No"),
            chequeDate: Yup.date()
                .nullable()
                .typeError("Cheque Date is required")
                .required("Cheque Date is required"),
            bankName: bankNameRule,
        }),
        fields: [
            { name: "chequeNo", label: "Cheque No", kind: "text", col: "col-sm-6", inputMode: "numeric", maxLength: 18 },
            { name: "chequeDate", label: "Cheque Date", kind: "date", col: "col-sm-6" },
            { name: "bankName", label: "Bank Name", kind: "text", col: "col-12" },
        ],
    },
    RT: {
        title: "Enter RTGS Details",
        initial: { rtgsNo: "", bankName: "" },
        schema: Yup.object({
            rtgsNo: numberRule("RTGS No"),
            bankName: bankNameRule,
        }),
        fields: [
            { name: "rtgsNo", label: "RTGS No", kind: "text", col: "col-12", inputMode: "numeric", maxLength: 18 },
            { name: "bankName", label: "Bank Name", kind: "text", col: "col-12" },
        ],
    },
    DC: {
        title: "Enter Card Details",
        initial: { cardNo: "", cardType: "" },
        schema: Yup.object({
            cardNo: numberRule("Card No"),
            cardType: Yup.string().required("Card Type is required"),
        }),
        fields: [
            { name: "cardNo", label: "Card No", kind: "text", col: "col-sm-6", inputMode: "numeric", maxLength: 18 },
            { name: "cardType", label: "Card Type", kind: "select", col: "col-sm-6", options: ["Credit", "Debit"] },
        ],
    },
    UP: {
        title: "Enter UPI Details",
        initial: { upiNo: "", bankName: "" },
        schema: Yup.object({
            upiNo: Yup.string()
                .trim()
                .matches(upiOrVpaRegex, "Enter a valid 10-digit UPI Number or VPA (e.g., 1234567890@upi)")
                .required("UPI Number / VPA is required"),
            bankName: bankNameRule,
        }),
        fields: [
            { name: "upiNo", label: "UPI Number / VPA", kind: "text", col: "col-12" },
            { name: "bankName", label: "Bank Name", kind: "text", col: "col-12" },
        ],
    },
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const PaymentModalComponent = ({ selectedPaymentType, onClose, setPaymentDetails }) => {
    const config = PAYMENT_FORMS[selectedPaymentType];

    // Esc closes the dialog; the page behind does not scroll while it is open.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        document.body.classList.add("modal-open");
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.classList.remove("modal-open");
        };
    }, [onClose]);

    if (!config) return null;

    const handleSubmit = (modalValues) => {
        // trim text so "  HDFC " is saved as "HDFC"
        const cleaned = Object.fromEntries(
            Object.entries(modalValues).map(([key, value]) => [key, typeof value === "string" ? value.trim() : value])
        );
        setPaymentDetails(cleaned); // update parent
    };

    const renderField = (field, index, values, setFieldValue) => {
        const id = `payment-${field.name}`;

        return (
            <div className={field.col} key={field.name}>
                <label className="form-label" htmlFor={id}>{field.label}</label>

                {field.kind === "text" && (
                    <Field
                        id={id}
                        name={field.name}
                        className="form-control"
                        inputMode={field.inputMode}
                        maxLength={field.maxLength}
                        autoFocus={index === 0}
                        autoComplete="off"
                    />
                )}

                {field.kind === "select" && (
                    <Field as="select" id={id} name={field.name} className="form-select">
                        <option value="">Select Type</option>
                        {field.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </Field>
                )}

                {field.kind === "date" && (
                    <DatePicker
                        id={id}
                        // the form keeps an ISO string; the picker needs a Date
                        selected={values[field.name] ? new Date(values[field.name]) : null}
                        placeholderText="Select Date"
                        onChange={(date) => setFieldValue(field.name, date ? formatISODate(date) : null)}
                        className="form-control"
                        wrapperClassName="w-100"
                        dateFormat="dd-MM-yyyy"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        autoComplete="off"
                        onKeyDown={(event) => event.preventDefault()}
                    />
                )}

                <ErrorMessage name={field.name} component="div" className="text-danger" />
            </div>
        );
    };

    return (
        <div
            className="modal show fade d-block custom-modal-bg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-modal-title"
            tabIndex={-1}
        >
            <div className="modal-dialog modal-dialog-centered payment-modal">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="payment-modal-title">{config.title}</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body text-start">
                        <Formik
                            initialValues={config.initial}
                            validationSchema={config.schema}
                            onSubmit={handleSubmit}
                        >
                            {({ values, setFieldValue }) => (
                                <Form autoComplete="off" noValidate>
                                    <div className="row g-3">
                                        {config.fields.map((field, index) => renderField(field, index, values, setFieldValue))}
                                    </div>

                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary px-4">
                                            Save
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentModalComponent;